import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { headerAssets, iframeConfig, statusOptions } from '../common/config';
import InfiniteScroll from 'react-infinite-scroll-component';
import Header from '../overlays/Header';
import StatusDropdown from '../dashboard/StatusDropdown';
import Spinner from '../common/spinner';

const WarningModal = ({ openModal = false, status, onClose }) => {
  if (!openModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white-default  p-6 rounded-lg shadow-lg text-center">
        <h2 className="text-lg font-semibold mb-4">Status: {status}</h2>
        <p className="mb-4">
          This SKU is currently <strong>{status}</strong> and is not yet completed. Please wait until it is completed.
        </p>
        <button
          className="bg-spyne-blue text-white-default  px-4 py-2 rounded"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};
const Dashboard = () => {
    const [data, setData] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [currentStatus, setCurrentStatus] = useState("");
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
  
    const navigate = useNavigate();
  
    const fetchData = async (status, page) => {
      setLoading(true);
      try {
        let params = {
          page_no: page,
        };
        if (status !== 'all') {
          params = { ...params, status: status };
        }
        const response = await axios.get(
          `${process.env.REACT_APP_BASEURL_API_SPYNE}/3d/api/v2/sku/all`, {
            params: params
          }
        );
        if (response?.data?.status === 'success') {
          let newData = response?.data?.data;
          const filteredData = newData.filter(
            (item) => item?.status?.toLowerCase() !== "draft"
          );
          setData((prevData) => (page > 1 ? [...prevData, ...filteredData] : filteredData));  
          setHasMore(response.data.hasNextPage);
    }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
          fetchData(statusFilter?.key, pageNo);
    }, [statusFilter, pageNo]);

    const handleStatusChange = (newStatus) => {
      setStatusFilter(newStatus);
      setPageNo(1); 
      setData([]);
      setHasMore(true); 
    };
  
    const loadMoreData = () => {
      if (!loading && hasMore) {
        setPageNo((prevPage) => prevPage + 1);
      }
    };
  
    const handleCardClick = (sku_id, bg_id, status, original_bg) => {
        if(status?.toLowerCase()==='completed' || status?.toLowerCase()==='to serve'){
          if(original_bg){
            navigate(`/3d/v2?sku_id=${sku_id}&original=true`);
          }else{
            navigate(`/3d/v2?sku_id=${sku_id}`);
          }
        }
      else {
        setOpenModal(true);
        setCurrentStatus(status);
      }
    };
  
    // Function to format the timestamp
    const formatDate = (timestamp) => {
      const date = new Date(timestamp);

      const options = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        hour12: true,
      };
      
        return date.toLocaleDateString('en-US', options);
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
          case "completed":
            return "text-green-600";
          case "in_progress":
            return "text-yellow-600"; 
          case "failed":
            return "text-red-600"; 
          case "yet_to_start":
              return "text-blue-400"
          case "to_serve":
            return "text-purple-400"
          default:
            return "text-gray-500"; 
        }
      };
  
    // Filter data to exclude items with the status of 'draft'
    const filteredData = data.filter((item) => item?.status?.toLowerCase() !== 'draft');

    return (
      <div className='text-left'>
        <Header/>
        <main className=' rounded-[16px] m-4 lg:my-6 lg:mx-12  bg-white-default p-2 lg:p-6 '>
          <div className='border-[1.5px] border-blue-border rounded-[24px] bg-blue-background p-4 lg:py-12 lg:px-6 flex flex-col justify-center gap-[30px]'>
            <div className='text-dark-blue'>
                <h1 className='font-bold text-2xl md:text-[32px] leading-[40px] text-center '>3D Journey</h1>
                <p className=' text-center font-medium text-sm md:text-base  '>Robust Immersive Experience</p>
            </div>
            <div>
              <div className='flex flex-col gap-2 lg:flex-row lg:justify-between mb-[5px] px-[13.5px]'>
                <p className='text-base lg:text-xl font-semibold text-gray-title'>
                  Demo Shoots {`(${filteredData?.length})`}
                </p>
                <StatusDropdown
                  statusFilter={statusFilter}
                  setStatusFilter={handleStatusChange}
                  options={statusOptions}
                />
              </div>
              <InfiniteScroll
                dataLength={data.length}
                next={loadMoreData}
                hasMore={hasMore} 
                loader={<div className=' p-[13.5px] flex justify-center'><Spinner /></div>} // Spinner while loading data
                className='overflow-hidden'
              >
                {filteredData && filteredData?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[13.5px] p-2 md:p-[13.5px]">
                    {filteredData.map((item) => {
                      const status= iframeConfig?.status[item?.status];
                      const source= iframeConfig?.source[item?.source];
                      const url = `${process.env.REACT_APP_3D_PREFIX_URL}/3d?sku_id=${item?.sku_id}`
                      
                      return (
                        <div
                          key={item.id}
                          className="bg-white-default  cursor-pointer shadow-custom rounded-lg"
                          onClick={(e) => handleCardClick(item?.sku_id, item?.bg_id, status, item?.original_bg)}
                        >
                            <img src={item?.thumbnail || '/assets/images/sample_car.png'} className='rounded-t-lg w-full object-cover object-center aspect-video' alt='car'/>
                            <div className="px-4 py-2 leading-4 mt-2 flex flex-col gap-2 text-gray-title">
                            <h2 className="font-semibold text-base text-gray-title line-clamp-2 sm:line-clamp-1">{item?.name}</h2>
                            <div className="text-[14px] flex flex-col gap-2 w-full">
                              <p>SKU ID: {item?.sku_id}</p>
                              <p>Status: <span className={getStatusClass(item?.status)}>{status}</span></p>
                              <p>Source: {source}</p>
                              <p>Created On: {formatDate(item?.createdAt)}</p>
                              <p>Is Original: <span className={`px-2 py-1 rounded-sm text-xs ${item?.original_bg ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{item?.original_bg ? "Yes" : "No"}</span></p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : !loading ? (
                  <p className="text-center p-[13.5px]">
                    <span>No results</span>
                  </p>
                ): <></>}
              </InfiniteScroll>
              <WarningModal openModal={openModal} status={currentStatus}  onClose={() => setOpenModal(false)}/>
              </div>
          </div>
        </main>
        
      </div>
    );
  };  

export default Dashboard;
